import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';

import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { ImpostosImpressosSelecaoComponent } from '../../../../shared/components/impostos-impressos-selecao/impostos-impressos-selecao.component';
import { SelectPesquisavelComponent } from '../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import {
  PageScrollSpyComponent,
  ScrollSpySection,
} from '../../../../shared/components/page-scroll-spy/page-scroll-spy.component';

import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { TipoDepositoDevolucaoEnum } from '../../../../shared/enums/vendas/tipo-deposito-devolucao.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';

import { ConfigPedidoVendaService } from '../../../../shared/services/apis/vendas/config-pedido-venda.service';
import { LookupService } from '../../../../shared/services/apis/produto/produto-lookup.service';
import { ConfigPedidoVendaRequest } from '../../../../shared/services/requests/vendas/config-pedido-venda.request';
import {
  TipoDepositoDevolucao,
  TipoExibicaoSecaoPedido,
  TipoLancamentoEstoqueFabricado,
} from '../../../../shared/services/models/vendas/config-pedido-venda.model';
import { TipoImpostoImpressao } from '../../../../shared/services/models/vendas/config-orcamento.model';
import { LookupItemStruct } from '../../../../shared/services/structs/pessoa/lookup-item.struct';

@Component({
  selector: 'app-config-pedido-venda',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    NgxEditorModule,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
    PageScrollSpyComponent,
    ImpostosImpressosSelecaoComponent,
    SelectPesquisavelComponent,
  ],
  templateUrl: './config-pedido-venda.component.html',
  styleUrls: ['./config-pedido-venda.component.css'],
})
export class ConfigPedidoVendaComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Vendas;
  public form: FormGroup;
  public isLoading = false;
  public isSaving = false;

  private idConfigPedidoVenda = 0;

  // Só libera o Salvar depois que a configuração da empresa realmente chegou. Salvar com o
  // carregamento falho gravaria os defaults do formulário sobre o que está no banco.
  public configCarregada = false;

  // Rótulos dos radios e do select vêm do banco, como em config-orcamento (tipo_impressao_imagem)
  // e config-geral-vendas (tipo_validacao_estoque_negativo). A tela não os declara.
  public opcoesTipoExibicaoSecaoPedido: TipoExibicaoSecaoPedido[] = [];
  public opcoesTipoLancamentoEstoqueFabricado: TipoLancamentoEstoqueFabricado[] = [];
  public opcoesTipoDepositoDevolucao: TipoDepositoDevolucao[] = [];
  public opcoesTipoImpostoImpressao: TipoImpostoImpressao[] = [];
  public opcoesLocalizacao: LookupItemStruct[] = [];
  public impostosSelecionados: number[] = [];

  // Menu de navegação das seções, no padrão de config-orcamento e config-geral-vendas.
  public sections: ScrollSpySection[] = [
    { id: 'exibicao', label: 'Exibição' },
    { id: 'impressao', label: 'Impressão' },
    { id: 'regras-bloqueios', label: 'Regras e bloqueios' },
    { id: 'observacoes', label: 'Observações' },
  ];

  // Editor da Observação padrão. Criado no ngOnInit e destruído no ngOnDestroy — instância de
  // ngx-editor não sobrevive à saída da tela. O mesmo objeto vai para o menu e para a área editável.
  public editorObservacao!: Editor;

  // Ordem dos grupos definida pela story, igual à do orçamento. Sem link, imagem, tabela, código,
  // h4+ nem "limpar formatação".
  public toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3'] }],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  public readonly bloqueioEstoqueTooltip =
    'Não se aplica a pedidos recebidos via API, aplicativo móvel ou e-commerce — esses pedidos entram mesmo ' +
    'sem saldo suficiente.';

  public readonly impostosDesabilitadoApoio = 'Disponível apenas com o cálculo de impostos ativo.';

  public readonly volumesErro = 'Informe um número inteiro maior ou igual a zero.';

  constructor(
    private formBuilder: FormBuilder,
    private configService: ConfigPedidoVendaService,
    private lookupService: LookupService,
    private alertService: AlertService,
    private router: Router
  ) {
    this.form = this.formBuilder.group({
      // Exibição
      isAlertaEnderecoIncompleto: [true],
      isAlertaComissaoZerada: [false],
      isVisualizarContasReceber: [false],
      isExibirTagStatusPagamento: [true],
      idTipoExibicaoDetalhesVenda: [3],
      idTipoExibicaoDadosAdicionais: [3],
      idTipoExibicaoTransportador: [2],
      // Impressão
      isImprimirCodigoBarras: [false],
      isImprimirDescComplementar: [true],
      isImprimirCabecalhoProducao: [true],
      isImprimirCamposAssinatura: [false],
      isImprimirEnderecoEmpresa: [true],
      isImprimirImagemProdutos: [false],
      isImprimirNomeFantasia: [false],
      isCalcularImpostos: [false],
      isImprimirImpostosItens: [false],
      // Regras e bloqueios
      isVendedorObrigatorio: [true],
      isBloquearClienteAtraso: [false],
      isBloquearSemFormaRecebimento: [true],
      isConsiderarPesoEmbalagens: [false],
      isBloquearExclusaoOcorrencia: [true],
      isBloquearEstoqueInsuficiente: [false],
      idTipoLancamentoEstoqueFabricado: [1],
      quantidadeVolumesPadrao: [0, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      idTipoDepositoDevolucao: [1],
      idLocalizacaoEstoqueDevolucao: [null],
      // Observações
      observacaoPadrao: [''],
    });
  }

  ngOnInit(): void {
    this.resetScrollToTop();

    this.editorObservacao = new Editor();

    // As duas dependências condicionais reagem só à ação do usuário: o carregamento aplica os valores
    // com emitEvent:false e depois chama os mesmos métodos sem descartar o que veio do banco.
    this.form.get('isCalcularImpostos')?.valueChanges.subscribe(() => this.aplicarDependenciaImpostos(true));
    this.form.get('idTipoDepositoDevolucao')?.valueChanges.subscribe(() => this.aplicarRegrasDeposito(true));

    this.carregarDados();
  }

  ngOnDestroy(): void {
    this.editorObservacao?.destroy();
  }

  public get depositoEspecificoControl(): FormControl {
    return this.form.get('idLocalizacaoEstoqueDevolucao') as FormControl;
  }

  private carregarDados(): void {
    this.isLoading = true;
    forkJoin({
      config: this.configService.get().pipe(catchError(() => of(null))),
      lookups: this.configService.getLookups().pipe(catchError(() => of(null))),
      localizacao: this.lookupService.getLocalizacaoEstoqueSelect().pipe(catchError(() => of(null))),
    }).subscribe({
      next: res => {
        this.isLoading = false;

        if (res.lookups && !res.lookups.isError) {
          this.opcoesTipoExibicaoSecaoPedido = res.lookups.listTipoExibicaoSecaoPedido || [];
          this.opcoesTipoLancamentoEstoqueFabricado = res.lookups.listTipoLancamentoEstoqueFabricado || [];
          this.opcoesTipoDepositoDevolucao = res.lookups.listTipoDepositoDevolucao || [];
          this.opcoesTipoImpostoImpressao = res.lookups.listTipoImpostoImpressao || [];
        } else if (res.lookups?.isError) {
          this.alertService.show('Aviso', res.lookups.errorDescription, AlertType.warning);
        }

        if (res.localizacao && !res.localizacao.isError) {
          this.opcoesLocalizacao = res.localizacao.list || [];
        } else if (res.localizacao?.isError) {
          this.alertService.show('Aviso', res.localizacao.errorDescription, AlertType.warning);
        }

        if (res.config && !res.config.isError && res.config.configPedidoVenda) {
          const c = res.config.configPedidoVenda;
          this.idConfigPedidoVenda = c.idConfigPedidoVenda;
          this.form.patchValue(
            {
              isAlertaEnderecoIncompleto: c.isAlertaEnderecoIncompleto,
              isAlertaComissaoZerada: c.isAlertaComissaoZerada,
              isVisualizarContasReceber: c.isVisualizarContasReceber,
              isExibirTagStatusPagamento: c.isExibirTagStatusPagamento,
              idTipoExibicaoDetalhesVenda: c.idTipoExibicaoDetalhesVenda,
              idTipoExibicaoDadosAdicionais: c.idTipoExibicaoDadosAdicionais,
              idTipoExibicaoTransportador: c.idTipoExibicaoTransportador,
              isImprimirCodigoBarras: c.isImprimirCodigoBarras,
              isImprimirDescComplementar: c.isImprimirDescComplementar,
              isImprimirCabecalhoProducao: c.isImprimirCabecalhoProducao,
              isImprimirCamposAssinatura: c.isImprimirCamposAssinatura,
              isImprimirEnderecoEmpresa: c.isImprimirEnderecoEmpresa,
              isImprimirImagemProdutos: c.isImprimirImagemProdutos,
              isImprimirNomeFantasia: c.isImprimirNomeFantasia,
              isCalcularImpostos: c.isCalcularImpostos,
              isImprimirImpostosItens: c.isImprimirImpostosItens,
              isVendedorObrigatorio: c.isVendedorObrigatorio,
              isBloquearClienteAtraso: c.isBloquearClienteAtraso,
              isBloquearSemFormaRecebimento: c.isBloquearSemFormaRecebimento,
              isConsiderarPesoEmbalagens: c.isConsiderarPesoEmbalagens,
              isBloquearExclusaoOcorrencia: c.isBloquearExclusaoOcorrencia,
              isBloquearEstoqueInsuficiente: c.isBloquearEstoqueInsuficiente,
              idTipoLancamentoEstoqueFabricado: c.idTipoLancamentoEstoqueFabricado,
              quantidadeVolumesPadrao: c.quantidadeVolumesPadrao,
              idTipoDepositoDevolucao: c.idTipoDepositoDevolucao,
              idLocalizacaoEstoqueDevolucao: c.idLocalizacaoEstoqueDevolucao,
              observacaoPadrao: c.observacaoPadrao || '',
            },
            { emitEvent: false }
          );
          this.impostosSelecionados = res.config.listImpostoImpressao || [];
          this.configCarregada = true;
        } else if (res.config?.isError) {
          this.alertService.show('Aviso', res.config.errorDescription, AlertType.warning);
        } else {
          // O `catchError` acima devolve null quando o GET falha, e esse caso não cai em nenhum dos
          // ramos anteriores. Sem este aviso a tela abria silenciosamente com os defaults do
          // formulário, e um Salvar gravaria esses defaults sobre a configuração real da empresa.
          this.alertService.show(
            'Aviso',
            'Não foi possível carregar as configurações dos pedidos de venda. Recarregue a tela antes de salvar.',
            AlertType.warning
          );
        }

        // Espelha nos controles dependentes o estado que veio do banco, sem resetar nada.
        this.aplicarDependenciaImpostos(false);
        this.aplicarRegrasDeposito(false);

        this.resetScrollToTop();
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
        this.resetScrollToTop();
      },
    });
  }

  /**
   * Só faz sentido imprimir impostos quando eles são calculados. Com o cálculo desligado, a chave de
   * impressão fica desmarcada e desabilitada (e é gravada como falsa); ao religar o cálculo, ela volta
   * habilitada e desmarcada — `reset` é o que distingue a ação do usuário do reflexo do valor gravado.
   * A seleção de impostos em si é preservada e volta a valer quando o cálculo é reativado.
   */
  private aplicarDependenciaImpostos(reset: boolean): void {
    const impressao = this.form.get('isImprimirImpostosItens');
    if (!impressao) return;

    if (this.form.get('isCalcularImpostos')?.value) {
      impressao.enable({ emitEvent: false });
      if (reset) impressao.setValue(false, { emitEvent: false });
      return;
    }

    impressao.setValue(false, { emitEvent: false });
    impressao.disable({ emitEvent: false });
  }

  /**
   * "Qual depósito" só existe — e só é obrigatório — com "Depósito específico" selecionado. Ao trocar
   * para outra opção o campo é ocultado, deixa de ser exigido e o valor informado é descartado.
   */
  private aplicarRegrasDeposito(limpar: boolean): void {
    const localizacao = this.form.get('idLocalizacaoEstoqueDevolucao');
    if (!localizacao) return;

    if (this.form.get('idTipoDepositoDevolucao')?.value === TipoDepositoDevolucaoEnum.DepositoEspecifico) {
      localizacao.setValidators([Validators.required]);
      localizacao.updateValueAndValidity({ emitEvent: false });
      return;
    }

    localizacao.clearValidators();
    if (limpar) localizacao.setValue(null, { emitEvent: false });
    localizacao.updateValueAndValidity({ emitEvent: false });
  }

  private resetScrollToTop(): void {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }

  public get mostrarImpostos(): boolean {
    return !!this.form.get('isCalcularImpostos')?.value && !!this.form.get('isImprimirImpostosItens')?.value;
  }

  public get mostrarDepositoEspecifico(): boolean {
    return this.form.get('idTipoDepositoDevolucao')?.value === TipoDepositoDevolucaoEnum.DepositoEspecifico;
  }

  public get volumesInvalido(): boolean {
    const volumes = this.form.get('quantidadeVolumesPadrao');
    return !!volumes?.invalid && (!!volumes.touched || !!volumes.dirty);
  }

  public onImpostosChange(ids: number[]): void {
    this.impostosSelecionados = ids;
    this.form.markAsDirty();
  }

  public salvar(): void {
    // Guarda também aqui, não só no botão: sem a configuração carregada, o PUT gravaria os defaults
    // do formulário e apagaria a observação padrão e a seleção de impostos da empresa.
    if (!this.configCarregada) {
      this.alertService.show(
        'Atenção',
        'As configurações não foram carregadas. Recarregue a tela antes de salvar.',
        AlertType.error
      );
      return;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.alertService.show('Atenção', 'Revise os campos destacados em vermelho.', AlertType.error);
      return;
    }

    this.isSaving = true;
    const raw = this.form.getRawValue();
    const depositoEspecifico = raw.idTipoDepositoDevolucao === TipoDepositoDevolucaoEnum.DepositoEspecifico;

    const request: ConfigPedidoVendaRequest = {
      // Exibição
      isAlertaEnderecoIncompleto: raw.isAlertaEnderecoIncompleto,
      isAlertaComissaoZerada: raw.isAlertaComissaoZerada,
      isVisualizarContasReceber: raw.isVisualizarContasReceber,
      isExibirTagStatusPagamento: raw.isExibirTagStatusPagamento,
      idTipoExibicaoDetalhesVenda: raw.idTipoExibicaoDetalhesVenda,
      idTipoExibicaoDadosAdicionais: raw.idTipoExibicaoDadosAdicionais,
      idTipoExibicaoTransportador: raw.idTipoExibicaoTransportador,
      // Impressão
      isImprimirCodigoBarras: raw.isImprimirCodigoBarras,
      isImprimirDescComplementar: raw.isImprimirDescComplementar,
      isImprimirCabecalhoProducao: raw.isImprimirCabecalhoProducao,
      isImprimirCamposAssinatura: raw.isImprimirCamposAssinatura,
      isImprimirEnderecoEmpresa: raw.isImprimirEnderecoEmpresa,
      isImprimirImagemProdutos: raw.isImprimirImagemProdutos,
      isImprimirNomeFantasia: raw.isImprimirNomeFantasia,
      isCalcularImpostos: raw.isCalcularImpostos,
      isImprimirImpostosItens: raw.isCalcularImpostos && raw.isImprimirImpostosItens,
      // Regras e bloqueios
      isVendedorObrigatorio: raw.isVendedorObrigatorio,
      isBloquearClienteAtraso: raw.isBloquearClienteAtraso,
      isBloquearSemFormaRecebimento: raw.isBloquearSemFormaRecebimento,
      isConsiderarPesoEmbalagens: raw.isConsiderarPesoEmbalagens,
      isBloquearExclusaoOcorrencia: raw.isBloquearExclusaoOcorrencia,
      isBloquearEstoqueInsuficiente: raw.isBloquearEstoqueInsuficiente,
      idTipoLancamentoEstoqueFabricado: raw.idTipoLancamentoEstoqueFabricado,
      quantidadeVolumesPadrao: Number(raw.quantidadeVolumesPadrao),
      idTipoDepositoDevolucao: raw.idTipoDepositoDevolucao,
      // Fora de "Depósito específico" a localização informada é descartada.
      idLocalizacaoEstoqueDevolucao: depositoEspecifico ? raw.idLocalizacaoEstoqueDevolucao : null,
      // Observações
      observacaoPadrao: raw.observacaoPadrao,
      // A seleção é preservada mesmo com a impressão de impostos desligada.
      listImpostoImpressao: this.impostosSelecionados,
    };

    this.configService.update(this.idConfigPedidoVenda, request).subscribe({
      next: response => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show(
          'Sucesso',
          'Configurações salvas. Valem para os próximos pedidos.',
          AlertType.success
        );
        this.voltar();
      },
      error: error => {
        this.isSaving = false;
        this.alertService.show('Aviso', error || 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/vendas']);
  }
}
